import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { makeCreate, makeDeleteWithReturn, makeGetSingleFromApi, makeUpdate } from '../make-api'
import { FilteredCreditApplication } from '../../types/FilteredCreditApplication'
import { RequiredDocument } from '../../types/RequiredDocument'
import { FilteredWorksheet } from '../../types/FilteredWorksheet'
import { ValidatePromotionCode, PromotionCodeDto } from '../../types/PromoCode'
import { RefinancingCreditApplication } from '../../types/RefinancingCreditApplication'
import { MerchantDto } from '../../types/Merchant'
import {
  Applicant,
  CreditApplication,
  EditCreditApplicationSinAndLoanAmount,
  Worksheet,
} from '../../schemas/PrequalificationSchema'
import { PersonalLoanFundingInfoDto } from '../../types/PersonalLoanFundingInfoDto'
import { PersonalLoanFundingComputedDto } from '../../types/PersonalLoanFundingComputedDto'
import { AvailableCredit } from '../../types/AvailableCredit'
import { transformApplicantDataToApi, transformCreditApplicationToDraft } from './credit-transform'
import { CustomerFeedback } from '../../types/CustomerFeedback'
import { ComputeAmountForFrequencyDto } from '../../types/ComputeAmountForFrequencyDto'
import { ComputedAmountForFrequencyDto } from '../../types/ComputedAmountForFrequencyDto'

const creditApplicationApi = {
  postWorkSheet: makeCreate<Worksheet, FilteredWorksheet>('api/CreditApplication/{creditApplicationId}/Worksheet'),
  updateWorksheet: makeUpdate<Worksheet, FilteredWorksheet>('api/CreditApplication/{creditApplicationId}/Worksheet'),
  computeFunding: makeCreate<PersonalLoanFundingInfoDto, PersonalLoanFundingComputedDto>(
    'api/CreditApplication/{id}/computePersonalLoanFunding',
  ),
  computeAmount: makeCreate<ComputeAmountForFrequencyDto, ComputedAmountForFrequencyDto>(
    'api/CreditApplication/computeAmountForFrequency',
  ),
  getWorksheet: makeGetSingleFromApi<FilteredWorksheet>('api/CreditApplication/{creditApplicationId}/Worksheet'),

  postDraftCreditApplicationAndPrequalify: makeCreate<CreditApplication, FilteredCreditApplication>(
    'api/creditApplication/draftAndPrequalify',
    transformCreditApplicationToDraft,
  ),

  getFilteredCreditApplication: makeGetSingleFromApi<FilteredCreditApplication>('api/creditApplication/{id}'),
  postDocument: async (
    apiClient: AxiosInstance,
    data: FormData,
    creditApplicationId: string,
  ): Promise<RequiredDocument> => {
    const payload: AxiosRequestConfig<FormData> = {
      method: 'POST',
      url: `api/creditApplication/${creditApplicationId}/document`,
      data,
    }
    const response = await apiClient.request(payload)
    const ret = response.data
    return ret as RequiredDocument
  },
  postEmailResumeLink: makeCreate('api/messaging/{id}/emailResumeLink'),
  postQualify: makeCreate<FilteredCreditApplication>('api/creditApplication/{id}/qualify'),
  postCoapplicant: makeCreate<Applicant, FilteredCreditApplication>(
    'api/creditApplication/{creditApplicationId}/coapplicant',
    transformApplicantDataToApi,
  ),
  putCancelCreditApplication: makeUpdate<FilteredCreditApplication>('api/creditApplication/{id}/cancel'),
  removeCoApplicant: makeDeleteWithReturn<FilteredCreditApplication>('api/creditApplication/{id}/coapplicant'),
  putUpdateCreditApplication: makeUpdate<EditCreditApplicationSinAndLoanAmount>('api/creditApplication/{id}'),

  getRefinancingCreditApplication: makeGetSingleFromApi<RefinancingCreditApplication>(
    'api/creditApplication/{id}/refinancing',
  ),
  postRefinancingCreditApplication: makeCreate<RefinancingCreditApplication>('api/creditApplication/{id}/refinancing'),

  getMerchant: makeGetSingleFromApi<MerchantDto>('api/merchant/{id}'),
  validatePromotionCode: makeCreate<PromotionCodeDto, ValidatePromotionCode>('api/merchant/validatePromotionCode'),
  postResendElectronicSignatureLink: makeCreate('api/creditApplication/{id}/resendElectronicSignature'),
  getAvailableCredit: makeGetSingleFromApi<AvailableCredit>('api/creditApplication/{id}/availablecredit'),
  postCustomerFeedback: makeCreate<CustomerFeedback>('api/{creditApplicationId}/CustomerFeedback'),
  getCustomerFeedback: makeGetSingleFromApi<CustomerFeedback>('api/{creditApplicationId}/CustomerFeedback'),
}

export default creditApplicationApi
